import type { ImageTransforms } from '@opallabs/cassini'
import { assetCdnHost } from './assetCdnHost'
import * as errors from './errorMessages'
import placeholderImage from './placeholderImage'

type UnknownDimension = number | null | undefined
export type PreviewQueryParams = {
  width?: number
  height?: number
  cache?: boolean
}
export type ResizeQueryParams = ImageTransforms & {
  cache?: boolean
  page?: number
}
type RouteQueryParams = {
  preview: PreviewQueryParams
  resize: ResizeQueryParams
}
type RouteQueryParamKeys<T = RouteQueryParams> = {
  [K in keyof T]: readonly (keyof T[K])[]
}

/** only exported for use in tests */
export const EXPLICITLY_LARGE_NUMBER: number = 9999

const routes = {
  base: ':cdnHost/api/v1/asset_proxy/:uuid',
  download: ':cdnHost/api/v1/asset_proxy/download/:uuid',
  preview: ':cdnHost/api/v1/asset_proxy/preview/:uuid',
  resize: ':cdnHost/api/v1/asset_proxy/resize/:width/:height/:uuid',
  transcode: ':cdnHost/api/v1/asset_proxy/transcode/:uuid.:ext',
} as const

const permittedQueryParams: RouteQueryParamKeys = {
  preview: ['width', 'height', 'cache'],
  resize: ['gravity', 'fit', 'withMetaData', 'animated', 'cache', 'page'],
}

const isUnset = (val: unknown): val is null | undefined =>
  val === undefined || val === null

export const sanitizeQueryParams = <
  QueryParams extends Record<string, unknown>,
  Route extends keyof RouteQueryParams,
>(
  queryParams: QueryParams,
  route: Route
): Partial<RouteQueryParams[Route]> => {
  if (!(route in permittedQueryParams)) {
    return {}
  }

  const permittedForRoute = permittedQueryParams[route]
  const cleanParams: Record<string, unknown> = {}
  for (const [key, val] of Object.entries(queryParams)) {
    const hasPermittedParam = (permittedForRoute as string[]).includes(key)
    if (hasPermittedParam && !isUnset(val)) {
      cleanParams[key] = val
    }
  }

  return cleanParams as Partial<RouteQueryParams[Route]>
}

export const ensureQueryParamDefaults = <T extends Record<string, unknown>>(
  params: T,
  defaults: Partial<T>
): Partial<T> => {
  return Object.keys({ ...defaults, ...params }).reduce<Partial<T>>(
    (acc: Partial<T>, key: keyof T) => {
      acc[key] =
        isUnset(params[key]) && key in defaults ? defaults[key] : params[key]
      return acc
    },
    {}
  )
}

const buildQueryString = (queryParams: Record<string, unknown> = {}) => {
  const queryString = Object.entries(queryParams).reduce((qs, [key, val]) => {
    const separator = qs.length ? '&' : ''
    return qs + `${separator}${key}=${val}`
  }, '')

  return queryString.length ? `?${queryString}` : ''
}

export const validateImageDimensions = (
  width?: UnknownDimension,
  height?: UnknownDimension
): readonly [adjustedWidth: number | null, adjustedHeight: number | null] => {
  let adjustedWidth = width
  let adjustedHeight = height

  // If both are unset, early exit with both null
  if (isUnset(adjustedWidth) && isUnset(adjustedHeight)) {
    return [null, null]
  }

  // From here, at least one of width or height is a number

  if (adjustedWidth === undefined) {
    throw new Error(errors.missingWidth)
  }
  if (adjustedHeight === undefined) {
    throw new Error(errors.missingHeight)
  }
  if (adjustedWidth === null) {
    adjustedWidth = EXPLICITLY_LARGE_NUMBER
  } else if (adjustedWidth <= 0) {
    throw new Error(errors.invalidWidth)
  }
  if (adjustedHeight === null) {
    adjustedHeight = EXPLICITLY_LARGE_NUMBER
  } else if (adjustedHeight <= 0) {
    throw new Error(errors.invalidHeight)
  }

  return [adjustedWidth, adjustedHeight]
}

type ImageUrlParams = {
  height?: UnknownDimension
  queryParams?: ResizeQueryParams
  uuid: string
  width?: UnknownDimension
}

export const assetProxyImageUrl = ({
  height,
  queryParams = {},
  uuid,
  width,
}: ImageUrlParams): string => {
  const defaults: Partial<ResizeQueryParams> = {
    animated: true,
    fit: 'cover',
    gravity: 'centre',
    withMetaData: false,
  }

  const sanitizedQueryParams = sanitizeQueryParams(queryParams, 'resize')
  const queryString = buildQueryString(
    ensureQueryParamDefaults(sanitizedQueryParams, defaults)
  )
  const [adjustedWidth, adjustedHeight] = validateImageDimensions(width, height)

  const cdnHost = assetCdnHost()
  if (isUnset(adjustedWidth) || isUnset(adjustedHeight)) {
    return (
      routes.base.replace(/:cdnHost/, cdnHost).replace(/:uuid/, uuid) +
      queryString
    )
  }

  return (
    routes.resize
      .replace(/:cdnHost/, cdnHost)
      .replace(/:width/, adjustedWidth.toString())
      .replace(/:height/, adjustedHeight.toString())
      .replace(/:uuid/, uuid) + queryString
  )
}

type PreviewUrlParams = Pick<PreviewQueryParams, 'cache'> & {
  uuid: string
  width?: UnknownDimension
  height?: UnknownDimension
}

export const assetProxyPreviewUrl = ({
  cache = true,
  height,
  uuid,
  width,
}: PreviewUrlParams): string => {
  const [adjustedWidth, adjustedHeight] = validateImageDimensions(width, height)

  // spread syntax omits width and height properties when undefined or null
  // only when cache param is false does it add it to query params, since default is truw
  const queryParams = {
    ...(adjustedWidth && { width: adjustedWidth }),
    ...(adjustedHeight && { height: adjustedHeight }),
    ...(!cache && { cache }),
  } satisfies PreviewQueryParams

  const sanitizedQueryParams = sanitizeQueryParams(queryParams, 'preview')
  const queryString = buildQueryString(sanitizedQueryParams)
  const cdnHost = assetCdnHost()

  return (
    routes.preview.replace(/:cdnHost/, cdnHost).replace(/:uuid/, uuid) +
    queryString
  )
}

type VideoUrlParams = {
  transcodingUnavailable?: boolean
  uuid: string
  width?: UnknownDimension
  height?: UnknownDimension
}

export const assetProxyVideoUrls = ({
  height,
  transcodingUnavailable = false,
  uuid,
  width,
}: VideoUrlParams): {
  previewUrl: string
  videoUrl: string
  mp4Url?: string
  webmUrl?: string
} => {
  const cdnHost = assetCdnHost()

  if (transcodingUnavailable) {
    return {
      previewUrl: placeholderImage,
      videoUrl: routes.base.replace(/:cdnHost/, cdnHost).replace(/:uuid/, uuid),
    }
  }

  const transcodeRoute = routes.transcode
    .replace(/:cdnHost/, cdnHost)
    .replace(/:uuid/, uuid)

  return {
    previewUrl: assetProxyPreviewUrl({ height, uuid, width }),
    mp4Url: transcodeRoute.replace(/:ext/, 'mp4'),
    webmUrl: transcodeRoute.replace(/:ext/, 'webm'),
    videoUrl: routes.base.replace(/:cdnHost/, cdnHost).replace(/:uuid/, uuid),
  }
}

type DownloadUrlParams = {
  uuid: string
}

export const assetProxyDownloadUrl = ({ uuid }: DownloadUrlParams): string => {
  const cdnHost = assetCdnHost()
  return routes.download.replace(/:cdnHost/, cdnHost).replace(/:uuid/, uuid)
}
